import React, { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import moment from "moment"
import "react-calendar/dist/Calendar.css"
import { useToast } from "~/utils/Toast"

import PageContainer from "~/library/PageContainer"
import PageTitle from "~/library/PageTitle"
import { ArrowLeft } from "lucide-react"
import CatchTable from "~/library/CatchTable"
import DateSelector from "~/library/DateSelector"
import CatchDropdown from "~/library/CatchDropdown"
import LoadingWrapper from "~/library/LoadingWrapper"
import ConfirmationModal from "~/library/ConfirmationModal"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiAreas } from "~/routes"
import type { AreaDetails } from "~/types/Area"
import {
  getAreasBackPath,
  getAreasBackTitle,
  clearAreasNavigationState,
  getAreasNavigationState,
} from "~/utils/navigationState"
import NavigationLink from "~/library/NavigationLink"
import Booking from "~/types/Booking"
import { formatCurrencyCents } from "~/utils/helpers"

const AreaView = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { id } = useParams()
  const { venueSlug } = getAppConfig()
  const navigate = useNavigate()
  const toast = useToast()

  const endpoint = apiAreas.show.path({ venueSlug, id })
  const { data: area, isLoading } = useFetchResource<{ area: AreaDetails }, "area">(
    endpoint,
    "area",
    {}
  )

  const getBackNavigation = () => {
    const navigationState = getAreasNavigationState()

    if (navigationState?.path && navigationState.path.includes("/clients/")) {
      return {
        path: navigationState.path,
        title: navigationState.title,
      }
    }

    return {
      path: getAreasBackPath(),
      title: getAreasBackTitle(),
    }
  }

  const deleteArea = () => {
    const req = apiAreas.destroy({
      data: {
        venue_slug: venueSlug,
        id: area?.id,
      },
    })
    req
      .then(() => {
        toast("Area deleted successfully", "success")
        clearAreasNavigationState()
        navigate("/areas")
      })
      .catch((res) => {
        toast(`${res.body?.errors?.join(", ") || "Please try again"}`, "danger")
      })
  }

  const renderDetail = (title: string, content: any, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const bookingsTable = (bookings: Booking[]) => {
    return (
      <CatchTable
        data={bookings}
        columns={[
          {
            key: "name",
            header: "Booking",
            formatter: (name: string, data: Booking) => (
              <NavigationLink type="bookings" to={`/bookings/${data.id}`}>
                {name}
              </NavigationLink>
            ),
          },
          {
            key: "date",
            header: "Date",
            formatter: (date: string) => moment(date).format("MMM Do YYYY"),
          },
          {
            key: "clientName",
            header: "Client",
          },
          {
            key: "payments",
            header: "Payments",
            formatter: (payments: number) => formatCurrencyCents(payments || 0),
          },
          {
            key: "anticipatedRevenueCents",
            header: "Anticipated Revenue",
            formatter: (antRev: number) => formatCurrencyCents(antRev),
          },
        ]}
      />
    )
  }

  return (
    <PageContainer>
      <LoadingWrapper isLoading={isLoading}>
        <PageTitle
          title={
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-link p-0 me-2"
                onClick={() => {
                  const backNav = getBackNavigation()
                  navigate(backNav.path)
                }}
                aria-label={`Go back to ${getAreasBackTitle()}`}
                title={`Go back to ${getAreasBackTitle()}`}
              >
                <ArrowLeft />
              </button>
              <span>{area?.name || "Loading..."}</span>
            </div>
          }
        >
          <CatchDropdown
            label="Actions"
            className="ms-2"
            items={[
              {
                label: "Edit Area",
                to: `/areas/${area?.id}/edit`,
              },
              {
                label: "Delete Area",
                onClick: () => setShowDeleteModal(true),
                className: "text-danger",
              },
            ]}
          />
        </PageTitle>
        <div className="row">
          <div className="col-6">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Upcoming Bookings</h4>
                {bookingsTable(area?.upcomingBookings || [])}
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Historical Bookings</h4>
                {bookingsTable(area?.historicalBookings || [])}
              </div>
            </div>
          </div>
          <div className="col-5">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Details</h4>
                <div className="row">
                  {renderDetail("Total Capacity", area?.capacity, 4)}
                  {renderDetail("Upcoming Bookings", area?.upcomingBookingsCount, 4)}
                  {renderDetail(
                    "Bookable Status",
                    <span
                      className={`badge badge-lg ${area?.bookable ? "bg-success" : "bg-warning"}`}
                    >
                      {area?.bookable ? "Available" : "Not Available"}
                    </span>,
                    4
                  )}
                  {renderDetail("Description", area?.description, 12)}
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <h4 className="card-title">Calendar</h4>
                <DateSelector
                  name="date"
                  isFormik={false}
                  disabledDates={area?.bookedDates || []}
                />
              </div>
            </div>
          </div>
        </div>
      </LoadingWrapper>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteArea}
        title={`Are you sure you want to delete "${area?.name}"?`}
        description={`This action cannot be executed if there are any bookings associated with this area. You can mark the area as not bookable instead, to hide it from public access.`}
      />
    </PageContainer>
  )
}
export default AreaView
